"use client";

import Image from "next/image";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { resolveMediaUrl } from "@/lib/media";
import { InquiryForm } from "@/components/forms/InquiryForm";
import type { TeamMemberList } from "@/lib/dto";
import { Section } from "@/components/ui/Section";

type Props = {
  team: TeamMemberList[];
  email: string | null | undefined;
};

/**
 * Mirrors production's FeaturedCallSchedule (website/src/components/
 * CallScheduleForm/index.js): avatar row, "Schedule a Call" CTA opening a
 * modal. Two intentional substitutions, both backend gaps, not frontend
 * shortcuts:
 * - Avatars: production's real 3 images come from CMS Settings media
 *   (fis-api.heavenhimalaya.com/media/global/setting--...), a field this
 *   backend's /api/public/settings doesn't expose at all. Team-member
 *   avatars are used instead as the closest available real data — see
 *   docs/tenant-setup.md for the gap.
 * - Modal backend: production's modal posts to /api/google-meet to book a
 *   real Google Calendar event, requiring live Google OAuth/service-account
 *   credentials this boilerplate doesn't have configured (deliberately not
 *   fabricated). The modal here uses the existing InquiryForm/Server Action
 *   instead, with its optional date field standing in for "preferred call
 *   time" — same visible flow (avatar row -> CTA -> modal -> submit -> real
 *   confirmation), different backend until real Meet credentials exist.
 */
export function ScheduleCall({ team, email }: Props) {
  const avatars = team.slice(0, 3);

  return (
    <section className="relative py-24 z-0 overflow-hidden bg-headings text-center after:absolute after:inset-0 after:bg-headings/70">
      {/* Lazy, responsive backdrop — a CSS background-image here was fetched
          eagerly at full size on every viewport. */}
      <Image
        src="/mountain.webp"
        alt=""
        fill
        sizes="100vw"
        className="-z-10 object-cover object-top"
      />
      <div className="relative z-10 mx-auto max-w-2xl px-4">
        {avatars.length ? (
          <ul className="mb-6 flex items-center justify-center [&>li+li]:-ml-4">
            {avatars.map((member) => {
              const src = member.avatar?.url
                ? resolveMediaUrl(member.avatar.url)
                : null;
              return (
                <li key={member.id}>
                  <span className="block h-[70px] w-[70px] overflow-hidden rounded-full border-[3px] border-white bg-headings">
                    {src ? (
                      <Image
                        src={src}
                        alt=""
                        width={80}
                        height={80}
                        className="h-full w-full object-cover"
                      />
                    ) : null}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}

        <p className="font-bold text-white">Got a Travel Question?</p>
        <h2 className="font-display mt-1 mb-3 text-2xl font-extrabold text-white md:text-3xl">
          Our Experts Have the Answers!
        </h2>
        <p className="max-w-[600px] text-xs md:text-sm pt-3 text-white/90 mx-auto leading-[1.6] font-medium tracking-wide">
          Dreaming of the Himalayas or planning your next great escape? Connect
          with one of our seasoned travel specialists for personalized advice,
          insider tips, and expert recommendations to make your journey
          unforgettable.
        </p>

        <Dialog.Root>
          <Dialog.Trigger className="mt-8 inline-flex min-h-[45px] items-center justify-center rounded bg-primary px-6 py-2 text-base font-bold text-paper transition-colors hover:bg-primary-dark">
            Schedule a Call
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=open]:fade-in" />
            <Dialog.Content className="fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:inset-x-auto sm:top-1/2 sm:bottom-auto sm:left-1/2 sm:w-[min(92vw,36rem)] sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-2xl">
              <div className="flex items-start justify-between">
                <Dialog.Title className="font-display text-xl font-bold text-headings">
                  Schedule a Call
                </Dialog.Title>
                <Dialog.Close
                  aria-label="Close"
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full text-muted-soft hover:bg-bg"
                >
                  <X size={18} aria-hidden />
                </Dialog.Close>
              </div>
              <p className="mt-1 text-sm text-muted">
                Tell us your preferred date and we&rsquo;ll get back to you to
                confirm a time.
              </p>
              <div className="mt-5">
                <InquiryForm defaultSubject="Schedule a call" enableDate />
              </div>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>

        {email ? (
          <p className="mt-6 text-sm font-bold text-white md:text-base">
            Email us at{" "}
            <a className="underline" href={`mailto:${email}`}>
              {email}
            </a>
          </p>
        ) : null}
      </div>
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-[-2px] z-20 h-6 rotate-180 text-bg"
      >
        <svg className="h-full w-full">
          <use xlinkHref="/icons.svg#brand_shape"></use>
        </svg>
      </span>
    </section>
  );
}
